app.service('clientStatementService', [ 'CONSTANTS', '$window', '$ngConfirm', 'portalHelper', 'HttpAuth',
  function (CONSTANTS, $window, $ngConfirm, portalHelper, HttpAuth) {
    const service = this;

    // Constants used
    service.REPORT_DOWNLOAD_FORMATS = {
      HTML: 'html',
      PDF: 'pdf',
      XLSX: 'xlsx'
    };

    service.QUERY_STRING_PARAMS = {
      CLIENT_ID: 'ci',
      START_DATE: 'sd',
      END_DATE: 'ed',
      GROUP_BY_PROJECT: 'gp',
      COMBINED_REPORT: 'cr',
      YEAR: 'yr',
      CLIENT: 'cc'
    };

    /***
     * Given the parameters, constructs the queryArgs object that is sent to the method to download
     * the report
     *
     * @param clientIds (required)
     * Array of client IDs for whom to generate the report
     *
     * @param format (required)
     * Format of the report - HTML, PDF or XLSX
     *
     * @param startDate (optional. Default null)
     * Licence start date
     *
     * @param endDate (optional. Default null)
     * Licence end date
     *
     * @param groupByProject (optional. Default false)
     * Whether report should group surveys by project.
     *
     * @param year (optional. Default null)
     * Year for which the report is to be generated
     *
     * @param clientId (optional. Default null)
     * Client id for which the report is to be generated
     *
     * @param combineToSingleReport (optional. Default false)
     * Whether output should be 1 file with all data combined or separate files within a zip file.
     *
     * @returns {{}}
     * The queryArgs object required by the report generation method.
     */
    service.construct_query_args = function (clientIds, format, startDate, endDate, groupByProject, year, client_id,
      combineToSingleReport) {

      const queryArgs = {};

      if (format == service.REPORT_DOWNLOAD_FORMATS.HTML) {
        queryArgs['body_only'] =  '1';
      }

      // Add client IDs to the get URL
      queryArgs['client_id'] = clientIds;

      // Start Date
      if (moment.isDate(startDate)) {
        queryArgs['licence_start_date_from'] = moment(startDate).format(CONSTANTS.dateTimeFormatMoment.API_DATE);
      }

      // End Date
      if (moment.isDate(endDate)) {
        queryArgs['licence_start_date_to'] = moment(endDate).format(CONSTANTS.dateTimeFormatMoment.API_DATE);
      }

      // Group surveys by project
      if (groupByProject && groupByProject == true) {
        queryArgs['group_by_project'] = '1';
      }

      // Year for which the report is to be generated
      if (year) {
        queryArgs['year'] = year;
      }

      // Client code for which the report is to be generated
      if (client_id) {
        queryArgs['client'] = client_id;
      }

      // Combine into single view
      if (combineToSingleReport && combineToSingleReport == true) {
        queryArgs['is_combined_report'] = '1';
      }

      return queryArgs;
    };

    /***
     * Returns the download URL for the Client Service Statement File given the format.
     *
     * @param format
     * The format of the data - HTML, PDF, XLSX
     *
     * @returns {string}
     * URL of the API to call for downloading the report
     */
    service.fetch_download_url = function (format) {
      return '/reports/client/statement/' + format + '/';
    };

    const showHtmlReportViaPopup = function (clientId, reportOptions) {
      const url = service.generate_html_report_url(
        [clientId],
        reportOptions.start_date,
        reportOptions.end_date,
        reportOptions.group_surveys_by_project,
        true);

      portalHelper.popup_window_open(url);
    };

    const downloadReport = function (format, clientId, reportOptions) {
      if (format === service.REPORT_DOWNLOAD_FORMATS.HTML) {
        portalHelper.showPopupError('An error occurred',
          'HTML reports cannot be downloaded.');

        return;
      }

      const downloadUrl = service.fetch_download_url(format);

      const queryArgs = service.construct_query_args(
        [clientId],
        format,
        reportOptions.start_date,
        reportOptions.end_date,
        reportOptions.group_surveys_by_project,
        true);

      portalHelper.showToast(CONSTANTS.typeOfToasts.SUCCESS,
        'The file will be downloaded momentarily.',
        'Request successful');

      // Actually download the file here for PDF & Excel
      const newTabUrl = portalHelper.prepareDownloadUrl(
        downloadUrl,
        queryArgs
      );
      window.open(newTabUrl, '_blank');
    };

    /***
     * Shows a pop up (for the Manage Clients Page) which presents download options for the
     * Client Statement report for the specified client.
     *
     * @param clientId {number} (required) The Id of the Client for whom the pop up is to be shown.
     * @param scope {object} (required) The scope of the parent page to render the pop up in.
     */
    service.show_client_statement_options_popup = function (clientId, scope) {
      scope.client_statement_report_options = {
        start_date: null,
        end_date: null,
        group_surveys_by_project: true
      };

      // Date Options
      scope.date_options = {
        maxDate: new Date(),
        startingDay: 1,
        showWeeks: false,
        popupPlacement: 'bottom'
      };

      $ngConfirm({
        title: 'Client Statement Options',
        contentUrl: portalHelper.getUrlRev('html_templates/dialogs/client_statement_options_popup.ecc30781.html'),
        columnClass: 'large',
        scope: scope,
        closeIcon: true,
        backgroundDismiss: true,
        buttons: {
          'Show Report': {
            btnClass: 'mat ui primary button',
            action: function (scope, button) {
              return showHtmlReportViaPopup(clientId, scope.client_statement_report_options);
            }
          },
          'Download Excel': {
            btnClass: 'mat ui primary button',
            action: function (scope, button) {
              return downloadReport(service.REPORT_DOWNLOAD_FORMATS.XLSX, clientId, scope.client_statement_report_options);
            }
          },
          'Download PDF': {
            btnClass: 'mat ui primary button',
            action: function (scope, button) {
              return downloadReport(service.REPORT_DOWNLOAD_FORMATS.PDF, clientId, scope.client_statement_report_options);
            }
          }
        }
      });
    };

    /***
     * Downloads the client statement report file - either Excel or PDF from the API server
     * or returns HTML data back via the callback method.
     *
     * @param clientIds {Array.<number>} (required) List of client Ids to generate the report for.
     * @param format {string} (required) Format of report. html, xlsx or pdf.
     * @param startDate {string} (optional) Start date from when you need the report in YYYY-MM-DD
     * @param endDate {string} (optional) End date until when you need the report in YYYY-MM-DD
     * @param groupByProject {boolean} Whether surveys should be grouped by projects
     * @param combineSingleView {boolean} Whether multiple reports should be combined into a single view
     * @param htmlCallback {function} When HTML data is requested, returns data via the callback method
     */
    service.download_report = function (clientIds, format, startDate,
      endDate, groupByProject, combineSingleView , htmlCallback) {
      const downloadUrl = service.fetch_download_url(format);
      const queryArgs = service.construct_query_args(
        clientIds,
        format,
        startDate,
        endDate,
        groupByProject,
        combineSingleView);

      if (format === service.REPORT_DOWNLOAD_FORMATS.HTML) {
        portalHelper.downloadFile(downloadUrl, queryArgs)
          .then(function (result) {
            if (htmlCallback != null && typeof(htmlCallback) === 'function') {
              htmlCallback(result.data);
            }
          })
          .catch(function(error) {
            portalHelper.showErrorCommon(error);
          });
      } else {
        portalHelper.downloadFileWithUrl(downloadUrl, queryArgs);
      }
    };

    /***
     * Generates the URL to call for the HTML Report View
     *
     * @param clientIds {Array.<number>} (required) List of client Ids to generate the report for.
     * @param startDate {string} (optional) Start date from when you need the report in YYYY-MM-DD
     * @param endDate {string} (optional) End date until when you need the report in YYYY-MM-DD
     * @param groupByProject {boolean} Whether surveys should be grouped by projects
     * @param year {number} (optional) Year for which the report is to be generated
     * @param client {number} (optional) Client code for which the report is to be generated
     * @param combineSingleView {boolean} Whether multiple reports should be combined into a single view
     */
    service.generate_html_report_url = function (clientIds, startDate, endDate, groupByProject, year, client_id,
      combineSingleView) {

      const params = [];
      params.push(service.QUERY_STRING_PARAMS.CLIENT_ID + '=' + clientIds.join(','));

      // Start date
      if (startDate != null && moment(startDate).isValid()) {
        params.push(service.QUERY_STRING_PARAMS.START_DATE + '=' +
          moment(startDate).format(CONSTANTS.dateTimeFormatMoment.API_DATE));
      }

      // End date
      if (endDate != null && moment(endDate).isValid()) {
        params.push(service.QUERY_STRING_PARAMS.END_DATE + '=' +
          moment(endDate).format(CONSTANTS.dateTimeFormatMoment.API_DATE));
      }

      // Group by Project
      if (groupByProject) {
        params.push(service.QUERY_STRING_PARAMS.GROUP_BY_PROJECT + '=1');
      }

      // Year
      if (year) {
        params.push(service.QUERY_STRING_PARAMS.YEAR + '=' + year);
      }

      // Client Code
      if (client_id) {
        params.push(service.QUERY_STRING_PARAMS.CLIENT + '=' + client_id);
      }

      // Single Report
      if (combineSingleView) {
        params.push(service.QUERY_STRING_PARAMS.COMBINED_REPORT + '=1');
      }

      const baseUrl = '/admin/#/Admin/Reports/ClientStatementReport?';

      return baseUrl + params.join('&');
    };
  }]);
